import React, { useState } from 'react';
import { WithdrawalRequest, WithdrawalFilters, Pagination } from '../../../types';
import { useWithdrawals } from '../../../hooks/useApi';
import FlaggedWithdrawalList from './FlaggedWithdrawalList';
import ProfitAnalysisPanel from './ProfitAnalysisPanel';
import ApproveModal from './ApproveModal';
import RejectModal from './RejectModal';
import AdjustBalanceModal from './AdjustBalanceModal';
import { PageHeader } from '@kioskgaming/ui';
import { toast } from 'react-hot-toast';

const FlaggedWithdrawalsManagement: React.FC = () => {
  const [filters, setFilters] = useState<WithdrawalFilters>({
    page: 1,
    limit: 20,
  });
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
  const [showProfitAnalysis, setShowProfitAnalysis] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAdjustBalanceModal, setShowAdjustBalanceModal] = useState(false);

  const { data: withdrawalsData, isLoading, isFetching, error, refetch } = useWithdrawals(filters);

  const withdrawals = withdrawalsData?.data?.withdrawals || [];
  const pagination: Pagination = withdrawalsData?.data?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  };

  const handleViewWithdrawal = (withdrawal: WithdrawalRequest) => {
    setSelectedWithdrawal(withdrawal);
    setShowProfitAnalysis(true);
  };

  const handleCloseProfitAnalysis = () => {
    setShowProfitAnalysis(false);
    setSelectedWithdrawal(null);
  };

  const handleApproveWithdrawal = (withdrawal: WithdrawalRequest) => {
    if (withdrawal.user?.shop_id || withdrawal.user?.shopId) {
      toast.error('Shop player: admin can only view, cannot approve/reject.');
      return;
    }
    setSelectedWithdrawal(withdrawal);
    setShowApproveModal(true);
  };

  const handleRejectWithdrawal = (withdrawal: WithdrawalRequest) => {
    if (withdrawal.user?.shop_id || withdrawal.user?.shopId) {
      toast.error('Shop player: admin can only view, cannot approve/reject.');
      return;
    }
    setSelectedWithdrawal(withdrawal);
    setShowRejectModal(true);
  };

  const handleAdjustBalance = (withdrawal: WithdrawalRequest) => {
    setSelectedWithdrawal(withdrawal);
    setShowAdjustBalanceModal(true);
  };

  const handleSuccess = () => {
    refetch();
  };

  const handleCloseModals = () => {
    setShowApproveModal(false);
    setShowRejectModal(false);
    setShowAdjustBalanceModal(false);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Unable to load flagged withdrawals</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Withdrawal Review"
        description="Review fraud-flagged withdrawals (abnormal profit) and other withdrawals requiring manual approval. Amount-limit reviews are operational, not profit fraud."
      />

      {/* Withdrawal List */}
      <FlaggedWithdrawalList
        withdrawals={withdrawals}
        pagination={pagination}
        isLoading={isLoading}
        isFetching={isFetching}
        appliedSort={{
          sortBy: pagination.sortBy ?? null,
          sortOrder: pagination.sortOrder ?? null,
        }}
        filters={filters}
        onFiltersChange={setFilters}
        onViewWithdrawal={handleViewWithdrawal}
        onApproveWithdrawal={handleApproveWithdrawal}
        onRejectWithdrawal={handleRejectWithdrawal}
        onRefresh={refetch}
      />

      {/* Profit Analysis Panel */}
      {showProfitAnalysis && selectedWithdrawal && (
        <ProfitAnalysisPanel
          withdrawal={selectedWithdrawal}
          onClose={handleCloseProfitAnalysis}
          onApprove={() => {
            handleCloseProfitAnalysis();
            handleApproveWithdrawal(selectedWithdrawal);
          }}
          onReject={() => {
            handleCloseProfitAnalysis();
            handleRejectWithdrawal(selectedWithdrawal);
          }}
          onAdjustBalance={() => {
            handleCloseProfitAnalysis();
            handleAdjustBalance(selectedWithdrawal);
          }}
        />
      )}

      {/* Modals */}
      <ApproveModal
        withdrawal={selectedWithdrawal}
        isOpen={showApproveModal}
        onClose={handleCloseModals}
        onSuccess={handleSuccess}
      />

      <RejectModal
        withdrawal={selectedWithdrawal}
        isOpen={showRejectModal}
        onClose={handleCloseModals}
        onSuccess={handleSuccess}
      />

      <AdjustBalanceModal
        withdrawal={selectedWithdrawal}
        isOpen={showAdjustBalanceModal}
        onClose={handleCloseModals}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default FlaggedWithdrawalsManagement;
